import PropTypes from 'prop-types'
import React from 'react'
import Slider from 'react-slick'
import { AmountOfPosts } from '../../lib/blog-utils/blog-post-widget-types';
import { BlogPost } from '../../models/BlogPost';

const sliderSettings = {
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1
}
let buttonClassName: string | undefined;
class BaseWidgetGroup extends React.Component<BaseWidgetGroupProp, { 
  isLoggedIn: boolean, 
  isLoading: boolean,
  posts: Array<BlogPost>,
  windowWidth: number,
  displayAmountOfCards: number,
}> {
  _isMounted: boolean = false;
  constructor(props:BaseWidgetGroupProp) {
    super(props)
    this.state = {
      isLoading: true,
      isLoggedIn: props.isSignedIn(),
      posts: [],
      windowWidth: window.innerWidth,
      displayAmountOfCards: 0
    }
    this.updateDimensions = this.updateDimensions.bind(this)
    this.fetchPosts = this.fetchPosts.bind(this)
  }

  componentDidMount() {
    this._isMounted = true
    this.fetchPosts()
    buttonClassName = this.props.buttonClassName
    window.addEventListener('resize', this.updateDimensions)
    this.updateDimensions()
  }

  componentWillUnmount() {
    this._isMounted = false
    window.removeEventListener('resize', this.updateDimensions)
  }

  componentDidUpdate() {
    if (this.props.isSignedIn() !== this.state.isLoggedIn) {
      this.setState({ isLoggedIn: this.props.isSignedIn() })
      this.fetchPosts()
    }
  }

  async fetchPosts() {
    this.setState({isLoading: true})
    const posts = await this.props.fetch()
    this.setState({
      posts: posts,
      isLoading: false
    })
  }

  updateDimensions() {
    let width = window.innerWidth
    this.updateNumOfPostsToShowInSlider(width)
    this.setState({ windowWidth: width })
  }

  updateNumOfPostsToShowInSlider(width: number) {
    if (width >= 1100) this.setState({displayAmountOfCards: this.props.amountOfPosts.big})
    else if (width > 600) this.setState({displayAmountOfCards: this.props.amountOfPosts.medium})
    else if (width <= 600) this.setState({displayAmountOfCards: this.props.amountOfPosts.small})
  }

  /*
  * Takes all posts and make them to BlogPostWidgetCard components. 
  * Makes every third into a big component and the remaining small.
  * Returns an empty div if posts have not been fetched yet.
  */
  private getBlogPostWidgetCard() {
    return (this.state.posts.length > 0) ? this.state.posts.map((post, index) => {
      return this.props.getCard(post, index)
    }
    ) : null
  }


  /*
  * Takes in current size of window and depending on size calls on appropriate settings for slider.
  * Slices the BlogPostWidgetCards correctly, and returns them in a slider.
  */
  composeSlider(blogPostWidgetCards: React.ReactNode[]) {
    if (this.state.windowWidth >= 600) {
      const sliderConfig = this.setSliderConfig()
      const content = this.arrangeBlogPosts(sliderConfig.displayAmountOfCards, blogPostWidgetCards)
      const className = "slider-wrapper " + this.props.class

      return this.buildSlider(className, sliderConfig.settings, content)
    }
    else {
      const sliderConfig = this.setSliderConfig()
      sliderConfig.settings = this.sliderSettingsSmallScreen()
      return this.buildSlider(this.props.wrapperClassName, sliderConfig.settings, blogPostWidgetCards)
    }
  }

  setSliderConfig() {
    if (this.state.windowWidth >= 1100) {
      return {
        displayAmountOfCards: this.props.amountOfPosts.big,
        settings: this.sliderSettingBigScreen()
      }
    }
    else {
      return {
        displayAmountOfCards: this.props.amountOfPosts.medium,
        settings: this.sliderSettingsSmallScreen()
      }
    }
  }

  buildSlider (className: string | undefined, settings: React.ComponentProps<typeof Slider>, content: React.ReactNode) {
    return (
      <div className={className}>
        <Slider {...settings}>
          {content}
        </Slider>
      </div>
    )
  }

  /*
  * Takes in all blog posts and divides them in chunks depending on numOfSlicesToShow.
  * Returns list of divs with BlogPostWidgetCards in.
  */
  arrangeBlogPosts(numOfSlicesToShow: number, blogPostWidgetCards: React.ReactNode[]) {
    let posts = []
    if (this.state.posts.length > 0) {
      let index = 0

      while (index < this.state.posts.length) {
        posts.push(
          <div className={this.props.wrapperClassName} key={index}>
            {blogPostWidgetCards.slice(index, index + numOfSlicesToShow)}
          </div>
        )
        index += numOfSlicesToShow
      }
      return posts
    }
  }

  sliderSettingBigScreen() {
    const settingsBig = {
      dots: false,
      nextArrow: <this.nextArrow/>,
      prevArrow: <this.prevArrow/>
    }

    return Object.assign({}, sliderSettings, settingsBig)
  }

  sliderSettingsSmallScreen() {
    let settingsSmall = {
      dots: true,
      swipeToSlide: true,
      useCss: true,
      dotsClass: 'stories-slider-dots',
      arrows: false
    }
    return Object.assign({}, sliderSettings, settingsSmall)
  }

  nextArrow(props: { onClick?: () => void }) {
    const { onClick } = props
    return (
      <a
        className={buttonClassName + '-next'}
        onClick={onClick}
      />
    )
  }

  prevArrow(props: { onClick?: () => void }) {
    const { onClick } = props
    return (
      <a
        className={buttonClassName + '-previous'}
        onClick={onClick}
      />
    )
  }

  render() {
    const blogPostWidgetCards = this.getBlogPostWidgetCard()
    if (blogPostWidgetCards && blogPostWidgetCards.length > 0) {
      return this.composeSlider(blogPostWidgetCards)
    }
    else {
      return <div className={"slider-wrapper " + this.props.class}>
        {this.props.getSpinner(this.state.windowWidth, this.state.displayAmountOfCards)}
      </div>
    }
  }
}

export interface BaseWidgetGroupProp {
  amountOfPosts: AmountOfPosts,
  fetch: () => Promise<BlogPost[]>,
  isSignedIn: () => boolean,
  getCard: (blogPost: BlogPost, index: number) => React.ReactNode,
  getSpinner: (windowWidth: number, displayAmountOfCards: number) => React.ReactNode,
  wrapperClassName?: string,
  buttonClassName?: string,
  class?: string
}
declare namespace BaseWidgetGroup { export var propTypes: any; }
BaseWidgetGroup.propTypes = {
    amountOfPosts: PropTypes.object.isRequired,
    fetch: PropTypes.func.isRequired,
    isSignedIn: PropTypes.func.isRequired,
    getCard: PropTypes.func.isRequired,
    getSpinner: PropTypes.func.isRequired,
    wrapperClassName: PropTypes.string,
    buttonClassName: PropTypes.string,
    class: PropTypes.string
}

export default (BaseWidgetGroup)
